import type { Dir } from "../demand/demandMath.ts";

/**
 * The contract between the day simulation (simulate.ts) and everything that
 * shows its result. Nothing in here depends on React or on the API types, so
 * the engine can be run from a test or a fixture as easily as from the page.
 */

// ── input ──────────────────────────────────────────────────────────────────

/** A stop of the route under simulation, in upward order. */
export interface SimStop {
  index: number;
  stopId: string;
  name: string;
  code: string;
}

/** One scheduled call of a trip at a stop. */
export interface SimCall {
  /** Index into SimInput.stops. */
  stop: number;
  /** Minutes after midnight; past 1440 for a call after midnight. */
  timeMin: number;
}

/** One trip of the timetable, as the simulation sees it. */
export interface SimTrip {
  id: string;
  /** The timetable's 1-based block (shift) number; 0 when it has none. */
  block: number;
  dir: Dir;
  /** In travel order: calls[0] is the trip's first stop. */
  calls: SimCall[];
}

export interface SimParams {
  /** Riders a bus carries, seated and standing. */
  busCapacity: number;
  /** Multiplier on the measured ridership; 1 is the ridership as measured. */
  demandFactor: number;
  /**
   * How long a rider waits before giving up and being counted as unserved.
   * Without it a saturated timetable would show riders queueing all day.
   */
  maxWaitMin: number;
}

/** Riders per day arriving at each stop during one time slice, by destination. */
export interface SimDemandBucket {
  startMin: number;
  /** od[o][d]: riders arriving at stop o in the slice to travel to stop d. */
  od: number[][];
}

export interface SimInput {
  stops: SimStop[];
  /** Width of each demand slice in minutes. */
  bucketMin: number;
  demand: SimDemandBucket[];
  trips: SimTrip[];
  params: SimParams;
}

// ── result ─────────────────────────────────────────────────────────────────

export interface TripCallResult {
  stop: number;
  timeMin: number;
  alighted: number;
  boarded: number;
  /** Riders who were waiting for this trip and found no room on it. */
  leftBehind: number;
  /** Riders aboard on leaving the stop. */
  load: number;
}

export interface TripResult {
  trip: SimTrip;
  calls: TripCallResult[];
  /** linkLoads[i] is riders aboard between calls[i] and calls[i + 1]. */
  linkLoads: number[];
  maxLoad: number;
  /** Index into linkLoads of the busiest link; -1 when the trip carried nobody. */
  maxLoadLink: number;
  /** maxLoad over bus capacity; never above 1, riders who do not fit are left behind. */
  loadFactor: number;
  /** Riders carried. */
  riders: number;
  /** Riders left behind over the whole trip. */
  leftBehind: number;
}

/** What riders at one stop, travelling one way, experienced over the day. */
export interface StopDirResult {
  stop: number;
  dir: Dir;
  /** Riders who came to the stop to travel this way. */
  arrivals: number;
  boarded: number;
  /** Riders who were left behind by a full bus at least once. */
  leftBehind: number;
  /** Times a rider was turned away by a full bus: a rider passed by two full buses counts twice. */
  turnedAway: number;
  /** Riders who never boarded: gave up, arrived after the last trip, or were still waiting at the end. */
  unserved: number;
  /** Of the unserved, riders who left after waiting maxWaitMin without getting on. */
  gaveUp: number;
  /** Mean wait of the riders who boarded, in minutes. */
  waitMeanMin: number;
  /** The longest any rider who boarded waited. */
  waitMaxMin: number;
  /** Riders who boarded after waiting longer than ten minutes. */
  waitOver10: number;
  /** Riders waiting at the start of each minute of the day (index = minute). */
  waiting: Float32Array;
  /** The most riders waiting at once while buses were still to come, and when. */
  maxWaiting: number;
  maxWaitingAt: number;
  /** Trips that called here going this way. */
  calls: number;
}

/** A full bus leaving riders at a stop. */
export interface LeftBehindEvent {
  timeMin: number;
  stop: number;
  dir: Dir;
  tripId: string;
  riders: number;
}

export interface SimTotals {
  arrivals: number;
  boarded: number;
  /** Riders left behind by a full bus at least once. */
  leftBehind: number;
  /** Times riders were turned away by a full bus, counting each bus. */
  turnedAway: number;
  /** Riders who never boarded; gaveUp is the part of them who ran out of patience. */
  unserved: number;
  gaveUp: number;
  waitMeanMin: number;
  waitMaxMin: number;
  waitOver10: number;
  /** Share of boarded riders who waited longer than ten minutes. */
  waitOver10Share: number;
  trips: number;
  /** Trips that left riders behind somewhere. */
  tripsFull: number;
  /** Trips whose busiest load was under a tenth of capacity. */
  tripsEmpty: number;
  /** Share of all trip-links carrying 85% of capacity or more. */
  linksCrowdedShare: number;
  /** Vehicles the timetable needs: its blocks, or the most trips running at once when it has none. */
  buses: number;
  /** Hours vehicles spend in service, from a block's first departure to its last arrival. */
  busHours: number;
  ridersPerBusHour: number;
  /** Rider-links carried over capacity-links offered: how much of the room on the road was used. */
  utilisation: number;
  /** The span of the simulated day: the first demand or trip to the last. */
  firstMin: number;
  lastMin: number;
}

export interface SimResult {
  input: SimInput;
  capacity: number;
  trips: TripResult[];
  /** One per stop and direction; see stopDirIndex(). */
  stopDirs: StopDirResult[];
  /** Every time a full bus left riders at a stop, in time order. */
  events: LeftBehindEvent[];
  /** Vehicles in service at the start of each minute of the day. */
  busesInService: Float32Array;
  /** Boarded riders by whole minutes waited: waitHistogram[m] waited at least m and under m + 1. */
  waitHistogram: number[];
  totals: SimTotals;
}

/** Position of stop `stop` travelling `dir` in SimResult.stopDirs. */
export function stopDirIndex(stop: number, dir: Dir): number {
  return stop * 2 + (dir === "upward" ? 0 : 1);
}
